/**
 * 拖拽对齐辅助线：页面轴/边、元素边/中心对齐，与栅格线统一在拖拽中吸附。
 */
import type { Element } from '@/components/canvas/document/publicTypes';
import type { Block } from '@/components/canvas/blocks/types';
import { getElementAABB } from '@/components/canvas/workspace/controllers/marqueeSelectUtils';
import { screenPxToWorld } from '@/components/canvas/selection/selectionChrome';
import type { ComputedGridGuides } from '@/components/canvas/workspace/overlays/gridGuides';

export const SNAP_THRESHOLD_SCREEN_PX = 8;

export type AlignmentGuideKind =
  | 'page-center'
  | 'page-edge'
  | 'element-center-page-edge'
  | 'element-align'
  | 'element-edge';

export interface AlignmentGuideLine {
  orientation: 'horizontal' | 'vertical';
  /** 竖线为 x，横线为 y */
  position: number;
  start: number;
  end: number;
  kind: AlignmentGuideKind;
}

export interface BoundsRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DragSnapInput {
  /** 拖拽起点各元素位置（页坐标） */
  startPositions: { id: string; x: number; y: number }[];
  rawDx: number;
  rawDy: number;
  elements: Element[];
  /** 页内功能块，作为吸附目标；拖拽 block 时也用于解析尺寸 */
  blocks?: Block[];
  pageWidth: number;
  pageHeight: number;
  gridGuides: ComputedGridGuides;
  zoom: number;
}

export interface DragSnapResult {
  dx: number;
  dy: number;
  guides: AlignmentGuideLine[];
}

function snapThresholdWorld(zoom: number): number {
  return screenPxToWorld(SNAP_THRESHOLD_SCREEN_PX, zoom);
}

function elementBounds(el: Element): BoundsRect {
  const { x, y, w, h } = getElementAABB(el);
  return { x, y, width: w, height: h };
}

function blockBounds(block: Block): BoundsRect {
  const { x, y, width, height } = block.layout;
  return { x, y, width, height };
}

function dragItemSize(
  id: string,
  elements: Element[],
  blocks: Block[],
): { w: number; h: number } {
  const el = elements.find((e) => e.id === id);
  if (el) {
    const { w, h } = getElementAABB(el);
    return { w, h };
  }
  const block = blocks.find((b) => b.id === id);
  if (block) {
    return { w: block.layout.width, h: block.layout.height };
  }
  return { w: 0, h: 0 };
}

/** 多选拖拽时的联合包围盒（应用 raw 偏移后） */
export function unionBoundsFromDragStarts(
  startPositions: { id: string; x: number; y: number }[],
  elements: Element[],
  dx: number,
  dy: number,
  blocks: Block[] = [],
): BoundsRect | null {
  if (startPositions.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const { id, x, y } of startPositions) {
    const { w, h } = dragItemSize(id, elements, blocks);
    const px = x + dx;
    const py = y + dy;
    minX = Math.min(minX, px);
    minY = Math.min(minY, py);
    maxX = Math.max(maxX, px + w);
    maxY = Math.max(maxY, py + h);
  }
  const width = maxX - minX;
  const height = maxY - minY;
  if (width <= 0 || height <= 0) return null;
  return { x: minX, y: minY, width, height };
}

function unionBoundsFromDragStartsAtOrigin(
  startPositions: { id: string; x: number; y: number }[],
  elements: Element[],
  blocks: Block[] = [],
): BoundsRect | null {
  return unionBoundsFromDragStarts(startPositions, elements, 0, 0, blocks);
}

type Axis = 'x' | 'y';

interface SnapCandidate {
  distance: number;
  value: number;
  guide: AlignmentGuideLine;
}

function trySnapX(
  box: BoundsRect,
  targetX: number,
  threshold: number,
  guide: AlignmentGuideLine,
): SnapCandidate[] {
  const { x, width } = box;
  const centerX = x + width / 2;
  const right = x + width;
  const out: SnapCandidate[] = [];
  const pairs: [number, number][] = [
    [x, targetX],
    [centerX, targetX],
    [right, targetX],
  ];
  const snaps = [targetX, targetX - width / 2, targetX - width];
  for (let i = 0; i < pairs.length; i++) {
    const d = Math.abs(pairs[i][0] - targetX);
    if (d <= threshold) {
      out.push({ distance: d, value: snaps[i], guide });
    }
  }
  return out;
}

function trySnapY(
  box: BoundsRect,
  targetY: number,
  threshold: number,
  guide: AlignmentGuideLine,
): SnapCandidate[] {
  const { y, height } = box;
  const centerY = y + height / 2;
  const bottom = y + height;
  const out: SnapCandidate[] = [];
  const pairs: [number, number][] = [
    [y, targetY],
    [centerY, targetY],
    [bottom, targetY],
  ];
  const snaps = [targetY, targetY - height / 2, targetY - height];
  for (let i = 0; i < pairs.length; i++) {
    const d = Math.abs(pairs[i][0] - targetY);
    if (d <= threshold) {
      out.push({ distance: d, value: snaps[i], guide });
    }
  }
  return out;
}

function pickBest(candidates: SnapCandidate[]): SnapCandidate | null {
  if (candidates.length === 0) return null;
  return candidates.reduce((a, b) => (a.distance <= b.distance ? a : b));
}

function spanY(a: BoundsRect, b: BoundsRect): { start: number; end: number } {
  return {
    start: Math.min(a.y, b.y),
    end: Math.max(a.y + a.height, b.y + b.height),
  };
}

function spanX(a: BoundsRect, b: BoundsRect): { start: number; end: number } {
  return {
    start: Math.min(a.x, b.x),
    end: Math.max(a.x + a.width, b.x + b.width),
  };
}

/** 拖拽框与其它矩形（元素 / block）的对齐与边缘贴近候选 */
function appendAlignmentCandidatesForOther(
  rawBox: BoundsRect,
  other: BoundsRect,
  threshold: number,
  xCandidates: SnapCandidate[],
  yCandidates: SnapCandidate[],
): void {
  const otherMidX = other.x + other.width / 2;
  const otherMidY = other.y + other.height / 2;
  const vx = spanY(rawBox, other);
  const hy = spanX(rawBox, other);

  for (const x of [other.x, otherMidX, other.x + other.width]) {
    xCandidates.push(
      ...trySnapX(rawBox, x, threshold, {
        orientation: 'vertical',
        position: x,
        start: vx.start,
        end: vx.end,
        kind: 'element-align',
      }),
    );
  }
  for (const y of [other.y, otherMidY, other.y + other.height]) {
    yCandidates.push(
      ...trySnapY(rawBox, y, threshold, {
        orientation: 'horizontal',
        position: y,
        start: hy.start,
        end: hy.end,
        kind: 'element-align',
      }),
    );
  }

  const edgePairs: [number, number, AlignmentGuideLine][] = [
    [
      rawBox.y,
      other.y + other.height,
      {
        orientation: 'horizontal',
        position: other.y + other.height,
        start: hy.start,
        end: hy.end,
        kind: 'element-edge',
      },
    ],
    [
      rawBox.y + rawBox.height,
      other.y,
      {
        orientation: 'horizontal',
        position: other.y,
        start: hy.start,
        end: hy.end,
        kind: 'element-edge',
      },
    ],
    [
      rawBox.x,
      other.x + other.width,
      {
        orientation: 'vertical',
        position: other.x + other.width,
        start: vx.start,
        end: vx.end,
        kind: 'element-edge',
      },
    ],
    [
      rawBox.x + rawBox.width,
      other.x,
      {
        orientation: 'vertical',
        position: other.x,
        start: vx.start,
        end: vx.end,
        kind: 'element-edge',
      },
    ],
  ];
  for (const [dragEdge, target, guide] of edgePairs) {
    const d = Math.abs(dragEdge - target);
    if (d <= threshold) {
      if (guide.orientation === 'horizontal') {
        const delta = target - dragEdge;
        yCandidates.push({
          distance: d,
          value: rawBox.y + delta,
          guide: { ...guide, position: target },
        });
      } else {
        const delta = target - dragEdge;
        xCandidates.push({
          distance: d,
          value: rawBox.x + delta,
          guide: { ...guide, position: target },
        });
      }
    }
  }
}

/**
 * 计算拖拽吸附与应显示的辅助线（页坐标）。
 */
export function computeDragSnap(input: DragSnapInput): DragSnapResult {
  const {
    startPositions,
    rawDx,
    rawDy,
    elements,
    blocks = [],
    pageWidth,
    pageHeight,
    gridGuides,
    zoom,
  } = input;

  const threshold = snapThresholdWorld(zoom);
  const draggingIds = new Set(startPositions.map((p) => p.id));
  const originUnion = unionBoundsFromDragStartsAtOrigin(startPositions, elements, blocks);
  if (!originUnion) {
    return { dx: rawDx, dy: rawDy, guides: [] };
  }

  const rawBox = unionBoundsFromDragStarts(startPositions, elements, rawDx, rawDy, blocks)!;
  const xCandidates: SnapCandidate[] = [];
  const yCandidates: SnapCandidate[] = [];

  const pageMidX = pageWidth / 2;
  const pageMidY = pageHeight / 2;

  // 1. 页面中心轴
  xCandidates.push(
    ...trySnapX(
      rawBox,
      pageMidX,
      threshold,
      {
        orientation: 'vertical',
        position: pageMidX,
        start: 0,
        end: pageHeight,
        kind: 'page-center',
      },
    ),
  );
  yCandidates.push(
    ...trySnapY(
      rawBox,
      pageMidY,
      threshold,
      {
        orientation: 'horizontal',
        position: pageMidY,
        start: 0,
        end: pageWidth,
        kind: 'page-center',
      },
    ),
  );

  // 2. 页面四边
  for (const x of [0, pageWidth]) {
    xCandidates.push(
      ...trySnapX(rawBox, x, threshold, {
        orientation: 'vertical',
        position: x,
        start: 0,
        end: pageHeight,
        kind: 'page-edge',
      }),
    );
  }
  for (const y of [0, pageHeight]) {
    yCandidates.push(
      ...trySnapY(rawBox, y, threshold, {
        orientation: 'horizontal',
        position: y,
        start: 0,
        end: pageWidth,
        kind: 'page-edge',
      }),
    );
  }

  // 3. 元素中心触及页面边缘
  const dragCenterX = rawBox.x + rawBox.width / 2;
  const dragCenterY = rawBox.y + rawBox.height / 2;
  for (const x of [0, pageWidth]) {
    if (Math.abs(dragCenterX - x) <= threshold) {
      xCandidates.push({
        distance: Math.abs(dragCenterX - x),
        value: rawBox.x + (x - dragCenterX),
        guide: {
          orientation: 'vertical',
          position: x,
          start: 0,
          end: pageHeight,
          kind: 'element-center-page-edge',
        },
      });
    }
  }
  for (const y of [0, pageHeight]) {
    if (Math.abs(dragCenterY - y) <= threshold) {
      yCandidates.push({
        distance: Math.abs(dragCenterY - y),
        value: rawBox.y + (y - dragCenterY),
        guide: {
          orientation: 'horizontal',
          position: y,
          start: 0,
          end: pageWidth,
          kind: 'element-center-page-edge',
        },
      });
    }
  }

  // 4 & 5. 与其他元素 / 功能块对齐
  for (const el of elements) {
    if (draggingIds.has(el.id) || el.interaction.locked || el.interaction.visible === false || el.layout.parentId) {
      continue;
    }
    appendAlignmentCandidatesForOther(rawBox, elementBounds(el), threshold, xCandidates, yCandidates);
  }
  for (const block of blocks) {
    if (draggingIds.has(block.id)) continue;
    appendAlignmentCandidatesForOther(rawBox, blockBounds(block), threshold, xCandidates, yCandidates);
  }

  // 栅格线（与旧 snapToGuides 行为一致，拖拽中吸附）
  for (const gX of gridGuides.vertical) {
    xCandidates.push(
      ...trySnapX(rawBox, gX, threshold, {
        orientation: 'vertical',
        position: gX,
        start: 0,
        end: pageHeight,
        kind: 'page-edge',
      }),
    );
  }
  for (const gY of gridGuides.horizontal) {
    yCandidates.push(
      ...trySnapY(rawBox, gY, threshold, {
        orientation: 'horizontal',
        position: gY,
        start: 0,
        end: pageWidth,
        kind: 'page-edge',
      }),
    );
  }

  const bestX = pickBest(xCandidates);
  const bestY = pickBest(yCandidates);

  const snappedX = bestX?.value ?? rawBox.x;
  const snappedY = bestY?.value ?? rawBox.y;
  const dx = snappedX - originUnion.x;
  const dy = snappedY - originUnion.y;

  const guides: AlignmentGuideLine[] = [];
  if (bestX) guides.push(bestX.guide);
  if (bestY) guides.push(bestY.guide);

  return { dx, dy, guides };
}
